import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useQuery } from 'react-apollo'
import { Redirect } from 'react-router-dom'
import classnames from 'classnames'
import qs from 'query-string'

import { requirePin, showPlanUpgradePopup } from '../../../actions'
import { switchProfileWithPin, switchProfile } from '../../../actions/profile'
import { LoadingModal } from '../../../components/loading/modal'
import {
  MODALS,
  NAVIGATION_BAR_GA_KIDS_PROFILE_SELECTION,
  PROFILES, WARNING_CREATE_KIDS_WITH_NO_PROFILE_SLOT
} from '../../../constants'
import { useIsAuthenticatedAndRedirect } from '../../../hooks/use-is-authenticated-and-redirect'
import { useProfilesEntitlement } from '../../../hooks/useProfilesEntitlement'
import { requirePinForSwitchProfile } from '../../../lib/account'
import { getModalLocation } from '../../../lib/modal'
import { hasHigherParentalRating } from '../../../lib/utils'
import { getProfileIsDisabled } from '../../../utils/getProfileIsDisabled'
import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'
import NavLinkWithQuery from '../../../components/shared/nav-link-with-query'
import Button from '../../../components/button'
import ProfileTile from '../components/profile-tile'
import AddProfileButton from '../components/add-profile-button'
import styles from './switch-profiles.css'

const isNoProfileSlotForNewKids = ({ location, profiles }) => {
  const parsedSearch = qs.parse(location.search)
  const allKids = profiles.filter(profile => profile.isKid)
  return Boolean(parsedSearch && parsedSearch.kids_full && allKids.length === 0)
}

/**
 * Displays available profiles.
 * Allows the user to switch profiles by clicking an avatar.
 * Allows the user to add a profile.
 * Allows the user to navigate to the profile management screen.
*/
function SwitchProfiles({ history, location }) {
  const dispatch = useDispatch()
  const {
    isAuthenticated,
    shouldRedirect,
    redirectTo
  } = useIsAuthenticatedAndRedirect(location)
  const accountQuery = useQuery(ACCOUNT_QUERY, {
    skip: !isAuthenticated
  })
  const account = accountQuery.data && accountQuery.data.account
  const { profiles, profileLimit, isProfileAllowBasicUpsell } = useProfilesEntitlement(accountQuery.data)

  const onAddProfileClick = useCallback(() => {
    const profilesAddLocation = getModalLocation(location, MODALS.qsParams.profilesAdd)
    const isPinRequired = requirePinForSwitchProfile(account)

    if (isPinRequired) {
      dispatch(requirePin(() => {
        history.push(profilesAddLocation)
      }))
      return
    }

    history.push(profilesAddLocation)
  }, [account, dispatch, history, location])

  const onSwitchProfile = useCallback((selectedProfile) => {
    const currentProfile = account.profiles.find(
      profile => profile.id === account.selectedProfile
    )

    const parsedSearch = qs.parse(location.search)
    const kidsFromNav = parsedSearch.nav && selectedProfile.isKid
    const isPinRequired = requirePinForSwitchProfile(account)

    if (
      isPinRequired &&
      hasHigherParentalRating(currentProfile.maxRating, selectedProfile.maxRating)
    ) {
      dispatch(switchProfileWithPin(selectedProfile))
      return
    }

    const gaOption = kidsFromNav ? NAVIGATION_BAR_GA_KIDS_PROFILE_SELECTION : null
    dispatch(switchProfile(selectedProfile, null, gaOption))
  }, [account, dispatch, location])

  if (shouldRedirect) {
    return <Redirect to={redirectTo} />
  }

  if (!account || accountQuery.loading || accountQuery.error) {
    return <LoadingModal />
  }

  const showUpgradePopup = () => {
    dispatch(showPlanUpgradePopup())
  }

  const onProfileClick = (isProfileDisabled, profile) => {
    if (isProfileDisabled) {
      showUpgradePopup()
    } else {
      onSwitchProfile(profile)
    }
  }

  const onAddIconClick = () => {
    if (isProfileAllowBasicUpsell) {
      dispatch(showPlanUpgradePopup())
    } else {
      onAddProfileClick()
    }
  }

  const isKidsNoProfileSlot = isNoProfileSlotForNewKids({ location, profiles })

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>Who’s watching NEON?</h2>
        <h3 className={styles.subTitle}>
          Create individual profiles and watchlists of your favourite shows
          for everyone in your household.
        </h3>
      </div>
      <div className={styles.profileList}>
        {
          profiles.map((profile, index) => {
            // Hide the current profile as it is already selected
            if (profile.id === account.selectedProfile) {
              return null
            }
            const isProfileDisabled = getProfileIsDisabled(
              isProfileAllowBasicUpsell, profileLimit, index
            )
            return (
              <div
                className={styles.profileListItem}
                key={profile.id}
              >
                <ProfileTile
                  profile={profile}
                  onAvatarClick={() => onProfileClick(isProfileDisabled, profile)}
                  isProfileDisabled={isProfileDisabled}
                />
              </div>
            )
          })
        }
        {
          profiles.length < PROFILES.maxNumber && (
            <div className={styles.profileListItem}>
              <AddProfileButton
                onClick={onAddIconClick}
                isProfileAllowBasicUpsell={isProfileAllowBasicUpsell}
              />
            </div>
          )
        }
      </div>

      {isKidsNoProfileSlot && (
        <div className={styles.warningMessage}>{WARNING_CREATE_KIDS_WITH_NO_PROFILE_SLOT}</div>
      )}

      <NavLinkWithQuery
        to={getModalLocation(location, MODALS.qsParams.profilesManagement)}
      >
        <Button
          className={classnames(
            styles.manageProfileButton,
            {
              [styles.withWarning]: isKidsNoProfileSlot
            }
          )}
          text="Manage Profiles"
          secondary
        />
      </NavLinkWithQuery>
    </div>
  )
}

SwitchProfiles.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired
}

export default SwitchProfiles
